import axios from 'axios';
import { supabase } from '../lib/supabase';

// Default to same-origin (Vercel API routes at /api/*). Overridable via env
// for local dev pointed at the standalone Express backend on port 4000.
const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({ baseURL: `${API_URL}/api` });

// Attach Supabase access token to every request so the API can gate
// per-page permissions server-side via _lib/auth.js.
api.interceptors.request.use(async (config) => {
  if (supabase) {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      // No session — request goes through without Bearer; protected routes will 401.
    }
  }
  return config;
});

// Global 401 handler. When the API returns 401 (stale Supabase access token,
// revoked session, etc.) we clear the local session and bounce the user to
// /signin with a `reason=session_expired` flag so SignIn can show a banner.
//
// Debounced at module scope: a page often fires multiple parallel requests
// (dashboard panels, permissions, etc.), and we only want to trigger the
// signOut + redirect once per page load — otherwise overlapping 401s would
// each call signOut() and assign() and we'd see flicker / duplicate logs.
let handling401 = false;
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    if (status === 401 && !handling401) {
      handling401 = true;
      try {
        if (supabase) {
          // Best-effort: revoke the local Supabase session so the next
          // page load doesn't try to reuse a known-bad token. Errors are
          // swallowed because we're already on the failure path.
          await supabase.auth.signOut().catch(() => {});
        }
      } finally {
        // Use assign() rather than replace() so the user can "back" to the
        // page they were on and retry post-sign-in. The SignIn page reads
        // `?reason=` from the URL and surfaces a one-line banner.
        if (typeof window !== 'undefined' && window.location.pathname !== '/signin') {
          window.location.assign('/signin?reason=session_expired');
        }
      }
    }
    return Promise.reject(error);
  }
);

// ── Dashboard (single request for all panels) ──
export const fetchDashboard = (env = 'mainnet', hours = 24) =>
  api.get('/dashboard', { params: { environment: env, hours } }).then(r => r.data);

// ── Transactions ──
export const fetchTransactions = (params) =>
  api.get('/transactions', { params }).then(r => r.data);

export const fetchTransactionSummary = (env, hours) =>
  api.get('/transactions/summary', { params: { environment: env, hours } }).then(r => r.data);

export const fetchCorridors = (env, hours) =>
  api.get('/transactions/corridors', { params: { environment: env, hours } }).then(r => r.data);

export const fetchRouteStats = (env, hours) =>
  api.get('/transactions/routes', { params: { environment: env, hours } }).then(r => r.data);

export const fetchTimeSeries = (env, hours) =>
  api.get('/transactions/timeseries', { params: { environment: env, hours } }).then(r => r.data);

// ── Wallets ──
export const fetchWallets = (env) =>
  api.get('/wallets', { params: { environment: env } }).then(r => r.data);

export const fetchWalletAlerts = (env) =>
  api.get('/wallets/alerts', { params: { environment: env } }).then(r => r.data);

// ── Treasury (A17 — float monitor, reversal queue, pending Safe co-signs) ──
// These endpoints front Firestore reads against:
//   system_health/float_status                  → current float gauge
//   float_status_transitions                    → last-N threshold events
//   reversals (where state in requested,in_progress) → reversal queue
//   safe_tx_queue (where state = awaiting_cosign)    → pending co-signs
//
// Backend handlers TBD when the dashboard API surface is added; this is the
// canonical client contract.
export const fetchTreasuryFloatStatus = () =>
  api.get('/treasury/float-status').then(r => r.data);

export const fetchTreasuryFloatTransitions = (limit = 50) =>
  api.get('/treasury/float-transitions', { params: { limit } }).then(r => r.data);

export const fetchTreasuryReversalQueue = (params = {}) =>
  api.get('/treasury/reversals', { params }).then(r => r.data);

export const fetchTreasuryPendingCosigns = () =>
  api.get('/treasury/safe-cosigns').then(r => r.data);

export const approveReversal = (reversalId, body) =>
  api.post(`/treasury/reversals/${reversalId}/approve`, body).then(r => r.data);

export const denyReversal = (reversalId, body) =>
  api.post(`/treasury/reversals/${reversalId}/deny`, body).then(r => r.data);

// ── Compliance ──
export const fetchComplianceFlags = (params) =>
  api.get('/compliance/flags', { params }).then(r => r.data);

export const fetchFlagSummary = () =>
  api.get('/compliance/flags/summary').then(r => r.data);

export const patchComplianceFlag = (id, body) =>
  api.patch(`/compliance/flags/${id}`, body).then(r => r.data);

// ── Integrations ──
export const fetchIntegrationsHealth = (env = 'mainnet') =>
  api.get('/integrations/health', { params: { environment: env } }).then(r => r.data);

export const fetchCircleBalances = (env = 'mainnet') =>
  api.get('/integrations/circle/balances', { params: { environment: env } }).then(r => r.data);

// ── Vendors (admin-gated page) ──
export const fetchVendorsSummary = (env = 'mainnet') =>
  api.get('/vendors/summary', { params: { environment: env } }).then(r => r.data);

export const fetchVendorTransactions = (vendorId, env = 'mainnet', hours = 24) =>
  api.get(`/vendors/${vendorId}/transactions`, { params: { environment: env, hours } }).then(r => r.data);

export const fetchVendorTimeseries = (vendorId, env = 'mainnet', hours = 24) =>
  api.get(`/vendors/${vendorId}/timeseries`, { params: { environment: env, hours } }).then(r => r.data);

export const fetchVendorIssues = (env = 'mainnet', hours = 24) =>
  api.get('/vendors/issues', { params: { environment: env, hours } }).then(r => r.data);

// Sandbox Console — pull recent transactions from the provider's REST
// API (Yellow Card or Paytrie). See api/vendors/[vendor]/refresh.js.
export const refreshVendorTransactions = (vendorId) =>
  api.post(`/vendors/${vendorId}/refresh`).then(r => r.data);

export const fetchSTRCandidates = () =>
  api.get('/compliance/str-candidates').then(r => r.data);

export const fetchComplianceReports = (type, env) =>
  api.get('/compliance/reports', { params: { type, environment: env } }).then(r => r.data);

export const updateFlagStatus = (id, data) =>
  api.patch(`/compliance/flags/${id}`, data).then(r => r.data);

// ── Support tickets ──
export const fetchSupportTickets = (params) =>
  api.get('/support/tickets', { params }).then(r => r.data);

export const fetchSupportTicket = (id) =>
  api.get(`/support/tickets/${id}`).then(r => r.data);

export const patchSupportTicket = (id, body) =>
  api.patch(`/support/tickets/${id}`, body).then(r => r.data);

// ── Careers tickets (job applications) ──
export const fetchCareersTickets = (params) =>
  api.get('/careers/tickets', { params }).then(r => r.data);

export const fetchCareersTicket = (id) =>
  api.get(`/careers/tickets/${id}`).then(r => r.data);

export const patchCareersTicket = (id, body) =>
  api.patch(`/careers/tickets/${id}`, body).then(r => r.data);

// ── Pitch deck access codes ──
export const fetchPitchAccessCodes = () =>
  api.get('/pitch-access/codes').then(r => r.data);

export const createPitchAccessCode = (body) =>
  api.post('/pitch-access/codes', body).then(r => r.data);

export const fetchPitchAccessCodeDetail = (code) =>
  api.get(`/pitch-access/codes/${encodeURIComponent(code)}`).then(r => r.data);

export const revokePitchAccessCode = (code, reason) =>
  api.delete(`/pitch-access/codes/${encodeURIComponent(code)}`, { data: { reason } }).then(r => r.data);

// Also need investor list for the "issue code to" dropdown
export const fetchInvestorsList = () =>
  api.get('/investors').then(r => r.data);

// ── Documents / data room ──
export const fetchDocuments = () =>
  api.get('/documents').then(r => r.data);

export const fetchDocumentPreviewUrl = (id) =>
  api.get(`/documents/${id}`).then(r => r.data);

// ── Investors ──
export const fetchInvestors = () =>
  api.get('/investors').then(r => r.data);

export const fetchInvestor = (id) =>
  api.get(`/investors/${id}`).then(r => r.data);

export const patchInvestor = (id, body) =>
  api.patch(`/investors/${id}`, body).then(r => r.data);

// ── Investor packets (signed link bundles) ──
export const createPacket = (body) =>
  api.post('/packets', body).then(r => r.data);

// ── Access log ──
export const fetchAccessLog = (params) =>
  api.get('/access-log', { params }).then(r => r.data);

// ── Admin audit log (security audit H-5) ──
// Immutable breach-canary trail of every admin-level action. The
// SELECT path is gated by has_page_access('settings'); writes are
// service-role only (no public INSERT/UPDATE/DELETE policy).
// See supabase/migrations/0015_admin_audit_log.sql + api/admin-audit.js.
export const fetchAdminAuditLog = (params) =>
  api.get('/admin-audit', { params }).then(r => r.data);

// ── Waitlist / Newsletter ──
// Pre-launch signups from coynitt.ca + per-row outreach state.
// See supabase/migrations/0013-0014 for schema, and api/waitlist/*.js
// for the server side. All four routes are gated by the 'waitlist'
// page permission server-side.
export const fetchWaitlist = (params) =>
  api.get('/waitlist', { params }).then(r => r.data);

export const fetchWaitlistSummary = () =>
  api.get('/waitlist/summary').then(r => r.data);

export const fetchWaitlistSignup = (id) =>
  api.get(`/waitlist/${id}`).then(r => r.data);

export const patchWaitlistSignup = (id, body) =>
  api.patch(`/waitlist/${id}`, body).then(r => r.data);

// ── WebSocket ──
// Same-origin WS when API_URL is empty (Vercel deploy); otherwise convert
// http(s) → ws(s) for the backend's /ws endpoint.
export const createWebSocket = () => {
  if (!API_URL) {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return new WebSocket(`${proto}://${window.location.host}/ws`);
  }
  const wsUrl = API_URL.replace(/^http/, 'ws') + '/ws';
  return new WebSocket(wsUrl);
};

export default api;
